import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Collapse, IconButton, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import { Code } from 'dashboard/components/common';
import { brandCnameTarget as BRAND_CNAME_TARGET } from 'config';

const copy = (text) => {
  try { navigator.clipboard.writeText(text); toast.success('Copied'); }
  catch (_) { toast.error('Copy failed'); }
};

function RecordLine({ type, name, value, note }) {
  return (
    <Box mb='10px'>
      <Stack direction='row' alignItems='center' spacing='8px' flexWrap='wrap'>
        <Chip size='small' label={type} sx={{ height: 20, fontSize: 11 }} />
        <Code>{name}</Code>
        <Typography variant='caption' color='text.secondary'>→</Typography>
        <Code>{value}</Code>
        <IconButton size='small' onClick={() => copy(value)}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
      {note && (
        <Typography variant='caption' color='text.secondary' display='block' ml='2px'>
          {note}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Remembers, per browser, that the owner finished the DNS side.
 *
 * Deliberately local rather than a field on the brand: nothing on the server
 * can actually confirm a wildcard CNAME is in place — the records are only
 * exercised when a visitor arrives — so this is a note-to-self, not a claim
 * about the world. Worst case it is forgotten on another device and the panel
 * opens expanded again, which is harmless.
 */
const doneKey = (domain) => `tpo_dns_done:${domain}`;

const readDone = (domain) => {
  try { return localStorage.getItem(doneKey(domain)) === '1'; } catch (_) { return false; }
};

/**
 * Section 2 — the records that actually send visitors here.
 *
 * Separate from verification on purpose. The TXT record proves ownership and
 * routes nothing; these route everything and prove nothing. Showing them as
 * one list of steps meant people published the TXT, saw "Verified", and were
 * left with a green badge and a domain that resolved nowhere.
 *
 * The wildcard is offered first deliberately. One record covers every
 * subdomain the brand will ever create here — including ones that do not
 * exist yet — so the owner never has to touch DNS again to add `shop`,
 * `help` or a future `dashboard`. Certificates are issued per hostname on
 * first request, so a wildcard certificate is not needed.
 */

export default function RoutingRecordsCard({ domain, verified }) {
  const [done, setDone] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!domain) return;
    const isDone = readDone(domain);
    setDone(isDone);
    setOpen(!isDone);
  }, [domain]);

  const markDone = (next) => {
    setDone(next);
    setOpen(!next);
    try {
      if (next) localStorage.setItem(doneKey(domain), '1');
      else localStorage.removeItem(doneKey(domain));
    } catch (_) { /* private mode — the panel just reopens next visit */ }
  };

  if (!domain) return null;

  return (
    <Box>
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' gap='12px' mb='14px' flexWrap='wrap'>
        <Box>
          <Stack direction='row' alignItems='center' spacing='8px'>
            <Typography variant='h6' fontWeight='700'>2. Point traffic here</Typography>
            {done && (
              <Chip
                size='small'
                color='success'
                icon={<CheckCircleIcon fontSize='small' />}
                label='Done'
              />
            )}
          </Stack>
          <Typography variant='body2' color='text.secondary'>
            {done
              ? 'You marked the DNS records as added. Expand if you need them again.'
              : <>Verification proves you own <Code>{domain}</Code>. These records are what actually bring visitors to it.</>}
          </Typography>
        </Box>
        {verified && (
          <Stack direction='row' spacing='4px' alignItems='center' flexShrink={0}>
            <Button size='small' onClick={() => markDone(!done)}>
              {done ? 'Not done' : 'Mark as done'}
            </Button>
            <IconButton size='small' onClick={() => setOpen((v) => !v)} aria-label={open ? 'Hide records' : 'Show records'}>
              <ExpandMoreIcon
                fontSize='small'
                sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              />
            </IconButton>
          </Stack>
        )}
      </Stack>

      {!verified ? (
        <Typography variant='body2' color='text.secondary'>
          Verify the domain above first.
        </Typography>
      ) : (
        <Collapse in={open}>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: '14px' }}>
            <Typography variant='body2' fontWeight='600' mb='8px'>
              For every subdomain — add this once
            </Typography>
            <Box ml='14px' mb='12px'>
              <RecordLine
                type='CNAME'
                name='*'
                value={BRAND_CNAME_TARGET}
                note='Covers shop, help, dashboard and anything you add later — no further DNS changes.'
              />
            </Box>

            <Typography variant='body2' fontWeight='600' mb='4px'>
              For the bare domain <Code>{domain}</Code>
            </Typography>
            <Typography variant='caption' color='text.secondary' display='block' mb='8px' ml='2px'>
              A wildcard does not cover the bare domain, and plain DNS forbids a CNAME at
              the apex — so providers offer this under a different name.
            </Typography>
            <Box ml='14px' mb='6px'>
              {/* Deliberately a hostname, never an IP address. Every brand that
                  hardcoded an IP would have to edit their DNS the day we move
                  servers or put a CDN in front — and they would find out by
                  going offline. Pointing at a name we control means we move and
                  nobody notices. */}
              <RecordLine
                type='ALIAS'
                name='@'
                value={BRAND_CNAME_TARGET}
                note='Called ALIAS, ANAME, or CNAME flattening depending on the provider — Cloudflare, Route53, DNSimple and most others have it.'
              />
            </Box>
            <Typography variant='caption' color='text.secondary' display='block' ml='14px'>
              No ALIAS option, or told <em>"ALIAS records are not allowed on a zone using
              DNSSEC"</em>? Leave the bare domain alone — the wildcard above already covers{' '}
              <Code>www.{domain}</Code>, so use your registrar's forwarding to send the bare
              domain there. That keeps DNSSEC on.
            </Typography>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
